import React, { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Typography, Button, List, message, Skeleton, Row, Col, Card, Space, Modal } from "antd"
import { PlusOutlined, EditOutlined, DeleteOutlined, RightOutlined, UploadOutlined } from "@ant-design/icons"
import { getMyProducts, deleteProduct } from "../services/ProductServices"
import "../assets/styles/ProductsBySeller.css"
import { useAuth } from "../contexts/authContext/index"

const { Title } = Typography
const { Meta } = Card

const SomeOfMyProducts = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { getToken } = useAuth()

  const fetchProducts = useCallback(async () => {
    try {
      const token = await getToken()
      const data = await getMyProducts(token)
      const normalizedProducts = data.map((product) => ({
        ...product,
        name: product.name || "Nombre desconocido",
        details: product.name.detalle || product.details,
        use: product.name.uso || product.use,
        imageUrl: product.images && product.images[0] ? product.images[0] : "",
      }))

      // Only keep the last 6 products
      setProducts(normalizedProducts.slice(-5))
      setLoading(false)
    } catch (error) {
      console.error("Error fetching products:", error)
      message.error("Failed to load products")
      setLoading(false)
    }
  }, [ getToken])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  const handleViewAllProducts = () => {
    navigate("/myproducts")
  }

  const handleAddProduct = () => {
    navigate("/add-product")
  }

  const handleBulkUpload = () => {
    navigate("/bulk-products")
  }

  const handleEditProduct = (productId) => {
    navigate(`/edit-product/${productId}`)
  }

  const handleDeleteProduct = async (productId) => {
    try {
      const token = await getToken()
      Modal.confirm({
        title: "¿Estás seguro de que quieres eliminar este producto?",
        content: "Esta acción no se puede deshacer.",
        okText: "Sí, eliminar",
        okType: "danger",
        cancelText: "No, cancelar",
        onOk: async () => {
          await deleteProduct(token, productId)
          message.success("Producto eliminado con éxito")
          fetchProducts() // Refresh the product list
        },
      })
    } catch (error) {
      console.error("Error deleting product:", error)
      message.error("Failed to delete product")
    }
  }

  const getImageType = (base64) => {
    if (base64.startsWith("data:image/png")) {
      return "image/png"
    } else if (base64.startsWith("data:image/jpeg")) {
      return "image/jpeg"
    } else {
      return "image" // Default if no match
    }
  }


  return (
    <div className="ProductsBySeller-page2">
      <Row justify="space-between" align="middle" className="page-header">
          <Col xs={24} sm={12}>
            <Title className="titled" level={2}>Algunos Productos:</Title>
          </Col>
          <Col xs={24} sm={12} className="add-product-col">
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProduct} className="filter-button">
                Agregar nuevo producto
              </Button>
              <Button type="default" icon={<UploadOutlined />} onClick={handleBulkUpload} className="filter-button">
                Carga masiva
              </Button>
            </Space>
          </Col>
        </Row>

      {loading ? (
        <Skeleton active />
      ) : products.length === 0 ? (
        <p>Ups, no hay nada aquí, agrega tu primer producto para ver toda su información.</p>
      ) : (
        <Row gutter={[16, 16]}>
          {products.map((product) => (
            <Col xs={24} sm={12} md={8} lg={6} key={product.name}>
              <Card
                className="product-card"
                cover={
                  <div className="image-container">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl || "/placeholder.svg"}
                        alt={product.name}
                        className="productt-image"
                        type={getImageType(product.imageUrl)}
                      />
                    ) : (
                      <div className="no-image">Sin imagen</div>
                    )}
                  </div>
                }
              >
                <div className="card-content">
                  <List.Item.Meta
                    title={<span className="card-title">{product.name}</span>}
                    description={
                      <span className="card-description">Detalle: {product.details || "No disponible"}</span>
                    }
                  />
                  <p className="card-use">Uso: {product.use || "No disponible"}</p>

                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => handleEditProduct(product.id)}
                      size="small"
                      type="primary"
                      className="edit-button"
                    >
                      Editar
                    </Button>
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteProduct(product.id)}
                      size="small"
                      type="danger"
                      className="delete-button"
                    >
                      Eliminar
                    </Button>
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              className="product-card view-all-card"
              onClick={handleViewAllProducts}
              hoverable
            >
              <div className="view-all-content">
                <RightOutlined className="view-all-icon" />
                <Meta
                  title="Ver todos"
                  className="view-all-title"
                />
                <p className="view-all-description">Explora tu catálogo completo</p>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default SomeOfMyProducts
